import React from 'react';
import PublicNav from '../../components/layout/PublicNav';
import PublicFooter from '../../components/layout/PublicFooter';
import PageMeta from '../../components/seo/PageMeta';
import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
  return (
    <>
      <PageMeta
        title="Privacy Policy | TRITAL Care®"
        description="Privacy Policy for TRITAL Care workforce platform."
        path="/privacy-policy"
      />
      
      <PublicNav />
      
      <main className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          {/* Header */}
          <div className="mb-12">
            <Link to="/" className="text-sm text-brand-navy hover:underline mb-4 inline-block">
              ← Back to Home
            </Link>
            <h1 className="font-display font-black text-4xl lg:text-5xl text-slate-900 tracking-tight">
              Privacy Policy
            </h1>
            <p className="mt-3 text-slate-600">
              Last updated: October 26, 2026
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-8">
              <p className="text-sm text-slate-700 mb-0">
                <strong>TRITAL Care®</strong> is the operating brand name of <strong>TOPTAL Care Inc.</strong>{' '}
                This Privacy Policy describes how we collect, use, disclose, and protect your personal information 
                when you use our workforce platform and services.
              </p>
            </div>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">1. Information We Collect</h2>
              
              <h3 className="font-semibold text-lg text-slate-900 mb-3 mt-6">Information You Provide</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                We collect information you directly provide when you:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
                <li>Create an account (name, email, password, phone number)</li>
                <li>Complete your profile (specialty, license information, experience)</li>
                <li>Submit credentials and certifications</li>
                <li>Apply for shifts or jobs</li>
                <li>Complete skill checklists</li>
                <li>Communicate with us or other users</li>
                <li>Contact our support team</li>
              </ul>

              <h3 className="font-semibold text-lg text-slate-900 mb-3 mt-6">Information Collected Automatically</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                When you use our platform, we automatically collect:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Device information (IP address, browser type, operating system)</li>
                <li>Usage data (pages visited, features used, time spent)</li>
                <li>Location data (if you grant permission)</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">2. How We Use Your Information</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                We use your information to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Provide and improve our services</li>
                <li>Match clinicians with appropriate shifts and facilities</li>
                <li>Process credential verifications</li>
                <li>Communicate with you about opportunities and updates</li>
                <li>Send administrative and account notifications</li>
                <li>Comply with legal and regulatory requirements</li>
                <li>Detect and prevent fraud or security issues</li>
                <li>Analyze platform usage and optimize user experience</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">3. SMS Communications</h2>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 mb-4">
                <p className="text-sm text-amber-900 mb-3">
                  <strong>Important: Your mobile information is protected</strong>
                </p>
                <p className="text-sm text-amber-900 mb-0">
                  When you provide your mobile number and opt in to SMS communications, your phone number and 
                  consent information are used ONLY to send you the communications you requested. We do NOT 
                  share or sell your mobile information to third parties for their marketing purposes.
                </p>
              </div>

              <h3 className="font-semibold text-lg text-slate-900 mb-3 mt-6">What We Send</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                If you opt in to SMS communications, we may send you:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
                <li>Job and shift opportunity notifications</li>
                <li>Account alerts and security notifications</li>
                <li>Credential expiration reminders</li>
                <li>Shift confirmations and schedule updates</li>
                <li>Platform updates and feature announcements</li>
              </ul>

              <h3 className="font-semibold text-lg text-slate-900 mb-3 mt-6">Your SMS Choices</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                <strong>Consent is Optional:</strong> You can use TRITAL Care without opting in to SMS. 
                Consent to receive text messages is NOT required for employment, platform access, or any services.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                <strong>Opt-Out Anytime:</strong> To stop receiving SMS, text STOP to any message, 
                update your settings, or contact us at{' '}
                <a href="mailto:support@tritalcare.com" className="text-brand-navy hover:underline">
                  support@tritalcare.com
                </a>.
              </p>
              <p className="text-slate-700 leading-relaxed">
                <strong>Message Frequency:</strong> Varies based on shift availability and your activity. 
                Message and data rates may apply from your carrier.
              </p>

              <h3 className="font-semibold text-lg text-slate-900 mb-3 mt-6">Third-Party Sharing Restrictions</h3>
              <p className="text-slate-700 leading-relaxed">
                <strong>Your mobile information will NOT be shared with third parties or affiliates for 
                marketing or promotional purposes.</strong> We only share SMS consent data with service 
                providers necessary to deliver the text messages you requested (such as our SMS platform provider).
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">4. Information Sharing</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                We share your information only in the following circumstances:
              </p>
              
              <h3 className="font-semibold text-lg text-slate-900 mb-3 mt-6">With Facilities and Employers</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                When you apply for a shift or job, we share relevant professional information 
                (credentials, experience, competency ratings) with the facility or employer.
              </p>

              <h3 className="font-semibold text-lg text-slate-900 mb-3 mt-6">Service Providers</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                We use third-party service providers for:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
                <li>Cloud hosting and storage</li>
                <li>Email and SMS delivery (consent data only)</li>
                <li>Payment processing</li>
                <li>Analytics and platform monitoring</li>
                <li>Customer support tools</li>
              </ul>

              <h3 className="font-semibold text-lg text-slate-900 mb-3 mt-6">Legal Requirements</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                We may disclose information if required by law, court order, or government request, 
                or to protect our rights and safety.
              </p>

              <h3 className="font-semibold text-lg text-slate-900 mb-3 mt-6">Business Transfers</h3>
              <p className="text-slate-700 leading-relaxed">
                In the event of a merger, acquisition, or sale of assets, your information may be 
                transferred to the acquiring entity.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">5. Data Security</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                We implement appropriate technical and organizational measures to protect your information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Encryption in transit (TLS 1.3) and at rest (AES-256)</li>
                <li>Role-based access controls</li>
                <li>Regular security audits and penetration testing</li>
                <li>SOC 2 Type II aligned security controls</li>
                <li>ISO 27001 aligned information security management</li>
                <li>HIPAA-aware handling of protected health information</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">6. Your Privacy Rights</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li><strong>Access:</strong> Request a copy of your personal information</li>
                <li><strong>Correction:</strong> Update inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your information (subject to legal retention)</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Data Portability:</strong> Receive your data in a structured format</li>
                <li><strong>Object:</strong> Object to processing based on legitimate interests</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-4">
                To exercise these rights, contact us at{' '}
                <a href="mailto:privacy@tritalcare.com" className="text-brand-navy hover:underline">
                  privacy@tritalcare.com
                </a>.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">7. Data Retention</h2>
              <p className="text-slate-700 leading-relaxed">
                We retain your information for as long as necessary to provide services, comply with legal 
                obligations, resolve disputes, and enforce agreements. Credential and compliance records may 
                be retained longer to meet regulatory requirements.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">8. Children's Privacy</h2>
              <p className="text-slate-700 leading-relaxed">
                Our platform is not intended for individuals under 18. We do not knowingly collect information 
                from children. If you believe we have collected information from a child, please contact us immediately.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">9. California Privacy Rights</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                California residents have additional rights under the California Consumer Privacy Act (CCPA):
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Right to know what personal information is collected</li>
                <li>Right to know whether information is sold or disclosed</li>
                <li>Right to opt-out of sale (we do not sell personal information)</li>
                <li>Right to deletion</li>
                <li>Right to non-discrimination for exercising privacy rights</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">10. International Users</h2>
              <p className="text-slate-700 leading-relaxed">
                Our services are based in the United States. If you access our platform from outside the US, 
                your information will be transferred to, stored in, and processed in the United States.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">11. Cookies and Tracking</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                We use cookies and similar technologies to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Remember your preferences and settings</li>
                <li>Analyze platform usage and performance</li>
                <li>Provide personalized content</li>
                <li>Measure advertising effectiveness</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-4">
                You can control cookies through your browser settings, but some features may not function properly.
              </p>
              <p className="text-slate-700 leading-relaxed mt-4">
                On our corporate recruitment pages, Google Analytics 4 measures aggregate page views, job-interest actions,
                and completed application events. We send public job identifiers, specialty categories, location states,
                and whether a résumé was attached; we do not intentionally send applicant names, email addresses,
                telephone numbers, résumés, home ZIP codes, or free-text application details to analytics.
                Google Analytics may use cookies and device-related signals under Google's privacy terms.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">12. Changes to This Policy</h2>
              <p className="text-slate-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of material changes 
                by email or through the platform. Continued use after changes constitutes acceptance.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">13. Contact Us</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                For questions about this Privacy Policy or your personal information:
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <p className="text-slate-700 mb-2">
                  <strong>Privacy Office</strong><br />
                  <strong>TOPTAL Care Inc. (d/b/a TRITAL Care®)</strong>
                </p>
                <p className="text-slate-700 mb-1">
                  5423 Pecan Fall PL<br />
                  Missouri City, TX 77459
                </p>
                <p className="text-slate-700 mb-1">
                  Phone: <a href="tel:+17267012273" className="text-brand-navy hover:underline">+1 (726) 701-2273</a>
                </p>
                <p className="text-slate-700 mb-0">
                  Email: <a href="mailto:privacy@tritalcare.com" className="text-brand-navy hover:underline">privacy@tritalcare.com</a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <PublicFooter />
    </>
  );
}
